/**
 * Générateurs des ITEMS NATIONAUX : français, 4e, Lexique, campagne 2026.
 * Même contrat que les modules de maths (2026/6e/automatismes.js, le modèle) : un générateur par item,
 * sa fiche en tête (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte,
 * CATEGORIES, generate.
 *
 * Le français est une matière de mots : une variante ne se calcule pas, elle se choisit dans une banque
 * ÉCRITE À LA MAIN, entrée par entrée (familles, étymologies, préfixes, suffixes, sens dans la phrase),
 * vérifiées au TLFi (CNRTL) et au Wiktionnaire ; un mot douteux, rare ou vieilli pour un élève de 4e est
 * écarté. Chaque entrée garde la tâche de l'item, sa difficulté et ses pièges : chaque mauvaise réponse
 * officielle a une raison, et l'entrée en fabrique une de même nature avec d'autres mots. L'item officiel
 * est toujours l'une des variantes. Une personne nommée n'est jamais reprise par « il » ou « elle ».
 */
import { randomChoice, shuffle, makeQcm, makeVraiFaux } from '../../../../utils';
import { normalizeAnswer } from '../../../../../utils/questionUtils';

const opts = (label) => ({ niveau: '4eme', matiere: 'francais', attendu: null, label });

/** Le second chemin : une variante mal construite lève une erreur au lieu de partir. */
const verifier = (condition, message) => {
    if (!condition) throw new Error(`Items de français 4e, lexique : ${message}`);
};

/** Le QCM d'une variante : quatre choix en texte, distincts pour le correcteur, le juste parmi eux. */
const qcm = (juste, faux, texte, label) => {
    const choix = [juste, ...faux];
    verifier(choix.length === 4 && choix.every((c) => typeof c === 'string' && c.length > 0), `quatre choix : ${choix.join(' | ')}`);
    verifier(new Set(choix.map(normalizeAnswer)).size === 4, `choix confondus : ${choix.join(' | ')}`);
    return makeQcm(choix, juste, texte, opts(label));
};

/** Un mot dont une partie est soulignée : [début, partie soulignée, fin]. */
const souligne = ([avant, partie, apres = '']) => `${avant}<u>${partie}</u>${apres}`;

/**
 * Q01 : Compléter une famille de mots par le radical commun.
 * Item : « Trouver un mot de la même famille que : « espérance, désespérer, espérer ». » ; esprit,
 *   espoir (juste), espace, espèce.
 * Domaine : trois mots d'une famille, le mot juste est un autre mot de la famille dont le radical change
 *   un peu de forme, comme espér- et espoir (chant- et chans-, bras et brac-, chaud et chauff-…) ; trois
 *   intrus commencent par les mêmes lettres que lui sans être de la famille, ni par le sens ni par
 *   l'origine (TLFi). 13 familles, dont celle de l'item.
 * Erreurs : esprit, espace et espèce commencent comme espoir (esp-) : le mot est choisi sur ses
 *   premières lettres, sans le sens du radical (espérer, c'est avoir de l'espoir). Chaque variante donne
 *   trois intrus qui commencent comme le mot juste (chance, changer, chandelle pour chanson).
 */
const L01 = 'Compléter une famille de mots par le radical commun';
/** [trois mots de la famille, le mot juste, trois intrus qui commencent comme lui] */
const FAMILLES_Q01 = [
    [['espérance', 'désespérer', 'espérer'], 'espoir', ['esprit', 'espace', 'espèce']],
    [['chanter', 'chanteur', 'enchanter'], 'chanson', ['chance', 'changer', 'chandelle']],
    [['bras', 'brassard', 'embrasser'], 'bracelet', ['braconnier', 'braise', 'branche']],
    [['chaud', 'chaleur', 'réchauffer'], 'chauffage', ['chauve', 'chausson', 'chaussée']],
    [['cheval', 'chevalier', 'chevaucher'], 'chevalerie', ['cheveu', 'chevreuil', 'cheville']],
    [['mur', 'muraille', 'emmurer'], 'muret', ['mûrir', 'murmure', 'muguet']],
    [['terre', 'terrain', 'enterrer'], 'territoire', ['terrible', 'terreur', 'terminer']],
    [['boire', 'buveur', 'imbuvable'], 'boisson', ['boîte', 'boiter', 'boisé']],
    [['fleur', 'fleurir', 'fleuriste'], 'floral', ['flotter', 'flocon', 'flou']],
    [['mourir', 'mourant', 'immortel'], 'mort', ['morceau', 'mordre', 'morale']],
    [['sel', 'salière', 'dessaler'], 'salé', ['salir', 'salon', 'saluer']],
    [['faim', 'affamé', 'affamer'], 'famine', ['famille', 'fameux', 'familier']],
    [['main', 'manuel', 'manier'], 'manuscrit', ['manger', 'manteau', 'mandarine']],
];
const genQ01 = () => {
    const [famille, juste, intrus] = randomChoice(FAMILLES_Q01);
    const debut = normalizeAnswer(juste).slice(0, 2);
    verifier(intrus.every((m) => normalizeAnswer(m).startsWith(debut)), `Q01, les intrus commencent comme ${juste}`);
    return qcm(juste, intrus, `<p>Trouver un mot de la même famille que : « ${famille.join(', ')} ».</p>`, L01);
};

/**
 * Q02 : Déduire le sens d'un suffixe de la formation des mots.
 * Item : « jardinage – arrosage – gommage – démontage », le suffixe -age souligné dans chaque mot ;
 *   « Le suffixe souligné permet de former un mot qui indique… » ; un contraire., un métier.,
 *   une action. (juste), une matière.
 * Domaine : quatre mots courants formés avec le même suffixe, souligné ; ce suffixe indique une action
 *   (-age, -ment, -ade), un métier (-ier, -iste, -ien) ou un instrument (-oir), sans autre sens possible
 *   dans la série (-erie, à la fois le commerce et la boutique, est écarté). 10 séries, dont celle de
 *   l'item.
 * Erreurs : « un contraire. » : le sens d'un préfixe prêté au suffixe (dé- de démontage), gardé dans
 *   chaque variante ; « un métier. » : le sens d'un mot voisin pris pour celui du suffixe (jardinage fait
 *   penser à jardinier) : chaque variante donne le sens voisin du sien (une action pour un métier ou un
 *   instrument, un métier pour une action) ; « une matière. » : erreur non identifiée avec certitude
 *   (le nom de départ, gomme, lu comme une matière ?), gardée telle quelle, TRANCHÉ (on garde).
 */
const L02 = 'Déduire le sens d\'un suffixe de la formation des mots';
/** [le sens juste, le sens voisin, quatre mots coupés en [début, suffixe]] */
const SERIES_Q02 = [
    ['une action.', 'un métier.', [['jardin', 'age'], ['arros', 'age'], ['gomm', 'age'], ['démont', 'age']]],
    ['une action.', 'un métier.', [['lav', 'age'], ['nettoy', 'age'], ['repass', 'age'], ['séch', 'age']]],
    ['une action.', 'un métier.', [['atterriss', 'age'], ['décoll', 'age'], ['frein', 'age'], ['dérap', 'age']]],
    ['une action.', 'un métier.', [['bricol', 'age'], ['coll', 'age'], ['découp', 'age'], ['ponç', 'age']]],
    ['une action.', 'un métier.', [['lance', 'ment'], ['range', 'ment'], ['déménage', 'ment'], ['entraîne', 'ment']]],
    ['une action.', 'un métier.', [['baign', 'ade'], ['gliss', 'ade'], ['promen', 'ade'], ['bouscul', 'ade']]],
    ['un métier.', 'une action.', [['pâtiss', 'ier'], ['cuisin', 'ier'], ['ferm', 'ier'], ['polic', 'ier']]],
    ['un métier.', 'une action.', [['fleur', 'iste'], ['dent', 'iste'], ['garag', 'iste'], ['journal', 'iste']]],
    ['un métier.', 'une action.', [['pharmac', 'ien'], ['électric', 'ien'], ['mécanic', 'ien'], ['informatic', 'ien']]],
    ['un instrument.', 'une action.', [['arros', 'oir'], ['ras', 'oir'], ['séch', 'oir'], ['hach', 'oir']]],
];
const genQ02 = () => {
    const [juste, voisin, mots] = randomChoice(SERIES_Q02);
    verifier(new Set(mots.map(([, suffixe]) => suffixe)).size === 1, 'Q02, un seul suffixe par série');
    return qcm(juste, ['un contraire.', voisin, 'une matière.'],
        `<p>${mots.map(([debut, suffixe]) => souligne([debut, suffixe])).join(' – ')}</p>`
        + '<p>Le suffixe souligné permet de former un mot qui indique…</p>', L02);
};

/**
 * Q03 : Trouver l'intrus parmi des verbes d'une même famille.
 * Item : « Voici des mots de la même famille. Trouver l'intrus. » ; engranger (juste), arranger,
 *   déranger, ranger.
 * Domaine : un verbe simple, deux verbes de sa famille formés avec un préfixe, et un intrus qui contient
 *   le verbe simple en entier mais vient d'un autre mot (en-g-ranger vient de grange, é-c-rouler de
 *   crouler, ba-lancer de balance). 10 familles, dont celle de l'item ; l'origine de chaque intrus
 *   vérifiée au TLFi (« démanger », « égarer », « alarmer », « comparer », de même origine que le verbe
 *   ou trop savants, écartés).
 * Erreurs : arranger, déranger, ranger : un vrai membre de la famille pris pour l'intrus, parce que le
 *   préfixe change le sens (arranger) ou cache le radical, pendant que l'intrus, qui a les mêmes
 *   lettres, passe pour un membre. Chaque variante garde le verbe simple et deux verbes préfixés.
 */
const L03 = 'Trouver l\'intrus parmi des verbes d\'une même famille';
/** [le verbe simple, deux verbes préfixés de sa famille, l'intrus qui contient le verbe simple] */
const FAMILLES_Q03 = [
    ['ranger', ['arranger', 'déranger'], 'engranger'],
    ['rouler', ['dérouler', 'enrouler'], 'écrouler'],
    ['lancer', ['relancer', 'élancer'], 'balancer'],
    ['lier', ['relier', 'délier'], 'plier'],
    ['ouvrir', ['rouvrir', 'entrouvrir'], 'couvrir'],
    ['voir', ['revoir', 'entrevoir'], 'devoir'],
    ['lacer', ['délacer', 'enlacer'], 'placer'],
    ['unir', ['réunir', 'désunir'], 'punir'],
    ['armer', ['désarmer', 'réarmer'], 'charmer'],
    ['longer', ['allonger', 'prolonger'], 'plonger'],
];
const genQ03 = () => {
    const [verbe, prefixes, intrus] = randomChoice(FAMILLES_Q03);
    verifier(intrus.includes(verbe) && intrus !== verbe, `Q03, ${intrus} contient ${verbe}`);
    return qcm(intrus, [...prefixes, verbe], '<p>Voici des mots de la même famille.</p><p>Trouver l\'intrus.</p>', L03);
};

/**
 * Q04 : Identifier les préfixes compatibles avec un verbe.
 * Item : « Le verbe « charger » accepte plusieurs préfixes. Trouver l'intrus. » ; sur-, in- (juste),
 *   dé-, re-.
 * Domaine : un verbe courant, trois préfixes qui forment avec lui un verbe connu d'un élève de 4e
 *   (surcharger, décharger, recharger ; surmonter, prélever, entrevoir, contredire, transmettre…) et un
 *   préfixe courant qui n'en forme aucun, même rare : dé- est écarté pour dire (se dédire), mener (se
 *   démener) et voir (devoir), in- pour passer (impasse) et former (informer). 13 verbes, dont celui de
 *   l'item.
 * Erreurs : sur-, dé-, re- : un préfixe qui convient, rejeté faute d'essayer le mot formé (surcharger
 *   est le moins courant des trois) ; chaque variante garde un préfixe moins attendu (sur-, pré-,
 *   entre-, contre-, trans-). L'intrus est un vrai préfixe (in-, sur-, dé-, pré-), qui ne se joint pas
 *   à ce verbe.
 */
const L04 = 'Identifier les préfixes compatibles avec un verbe';
/** [le verbe, trois préfixes qui forment un verbe connu, le préfixe intrus] */
const PREFIXES_Q04 = [
    ['charger', ['sur-', 'dé-', 're-'], 'in-'],
    ['monter', ['sur-', 'dé-', 're-'], 'in-'],
    ['passer', ['sur-', 'dé-', 're-'], 'pré-'],
    ['venir', ['sur-', 'pré-', 're-'], 'in-'],
    ['lever', ['pré-', 'en-', 're-'], 'dé-'],
    ['poser', ['ex-', 'dé-', 're-'], 'sur-'],
    ['prendre', ['sur-', 'entre-', 're-'], 'in-'],
    ['dire', ['contre-', 'pré-', 're-'], 'sur-'],
    ['tenir', ['con-', 'dé-', 're-'], 'sur-'],
    ['mettre', ['trans-', 'pro-', 're-'], 'sur-'],
    ['former', ['trans-', 'dé-', 're-'], 'sur-'],
    ['voir', ['entre-', 'pré-', 're-'], 'in-'],
    ['porter', ['trans-', 'em-', 're-'], 'sur-'],
];
const genQ04 = () => {
    const [verbe, bons, intrus] = randomChoice(PREFIXES_Q04);
    verifier(!bons.includes(intrus), `Q04, ${intrus} n'est pas un préfixe de ${verbe}`);
    return qcm(intrus, bons, `<p>Le verbe « ${verbe} » accepte plusieurs préfixes.</p><p>Trouver l'intrus.</p>`, L04);
};

/**
 * Q05 : Trouver le nom en -ette qui n'est pas un diminutif.
 * Item : « Les mots suivants sont formés avec le suffixe « -ette » qui signifie « petite », sauf pour un
 *   d'entre eux. Trouver l'intrus. » ; maisonnette, poulette, chouette (juste), jupette.
 * Domaine : trois diminutifs en -ette formés sur un nom FÉMININ courant, pour que « petite » se lise
 *   tel quel (petite maison, petite statue, petite cloche ; balayette, camionnette, sur des noms
 *   masculins, écartés), tirés parmi 16 ; un intrus parmi 6. Plus de 3 000 variantes.
 * Erreurs : maisonnette, poulette, jupette : un vrai diminutif rejeté, faute de vérifier « petite +
 *   nom » (poulette, une jeune poule, trompe le plus) ; chouette, l'intrus, se lit chou + -ette, « petit
 *   chou », sans vérification du sens. Chaque intrus se lit comme un mot connu + -ette sans en être le
 *   diminutif : un oiseau (mouette, « moue »), un objet (baguette, « bague »), ou un nom formé sur un
 *   verbe (allumette, sonnette, devinette). Écartés : fourchette, toilette, lunette, galette, pochette,
 *   qui sont d'anciens diminutifs (TLFi), où « petite » se discute.
 */
const L05 = 'Trouver le nom en -ette qui n\'est pas un diminutif';
const DIMINUTIFS_Q05 = [
    'maisonnette', 'poulette', 'jupette', 'fillette', 'statuette', 'chansonnette', 'planchette', 'voiturette',
    'chaînette', 'tartelette', 'clochette', 'caissette', 'fleurette', 'chambrette', 'réglette', 'bûchette',
];
const INTRUS_Q05 = ['chouette', 'mouette', 'baguette', 'allumette', 'sonnette', 'devinette'];
const genQ05 = () => {
    const diminutifs = shuffle(DIMINUTIFS_Q05).slice(0, 3);
    const intrus = randomChoice(INTRUS_Q05);
    verifier([...diminutifs, intrus].every((m) => m.endsWith('ette')), 'Q05, quatre noms en -ette');
    return qcm(intrus, diminutifs,
        '<p>Les mots suivants sont formés avec le suffixe « -ette » qui signifie « petite », sauf pour un d\'entre eux.</p>'
        + '<p>Trouver l\'intrus.</p>', L05);
};

/**
 * Q06 : Dire si deux mots sont de la même famille (tableau à cocher).
 * Item : « Indiquer si les mots proposés sont de la même famille ou non. » ; trois lignes : « carapace »
 *   et « car » (ne sont pas de la même famille), « dénouer » et « nœud » (sont de la même famille),
 *   « joyeux » et « joie » (sont de la même famille). Colonnes : « Sont de la même famille » et « Ne sont
 *   pas de la même famille », sans le point final de la capture, comme la bonne réponse en base.
 * Domaine : trois lignes, comme l'item, dans un ordre tiré : une paire de mots qui se ressemblent sans
 *   être de la même famille, le plus court contenu dans le plus long (12 paires, origines vérifiées au
 *   TLFi : pirate et pire, orage et or, pinceau et pince…) ; une paire de la même famille dont le radical
 *   change de forme, comme nœud et dénouer (8 paires : beau et embellir, œuvre et ouvrier, peuple et
 *   populaire… ; nuit et nocturne, réponse de Q09, écartée) ; une paire de la même famille au radical
 *   visible, comme joie et joyeux (8 paires).
 * Erreurs : carapace et car cochés « même famille » : la ressemblance des lettres prise pour une parenté
 *   (le sens n'est pas lu) ; dénouer et nœud cochés « pas de la même famille » : le radical qui change de
 *   forme (nou-, nœu-) n'est pas reconnu ; joyeux et joie : la même erreur, sur un radical plus visible.
 */
const L06 = 'Dire si deux mots sont de la même famille';
const COLONNES_Q06 = ['Sont de la même famille', 'Ne sont pas de la même famille'];
/** [le mot long, le mot court qu'il contient] : ils ne sont pas de la même famille. */
const FAUSSES_PAIRES_Q06 = [
    ['carapace', 'car'], ['courage', 'cour'], ['pirate', 'pire'], ['fourmi', 'four'], ['orage', 'or'],
    ['baleine', 'balle'], ['pinceau', 'pince'], ['château', 'chat'], ['baguette', 'bague'],
    ['coussin', 'cousin'], ['pistolet', 'piste'], ['bougie', 'bouger'],
];
/** Même famille, le radical change de forme. */
const PAIRES_ALTERNEES_Q06 = [
    ['dénouer', 'nœud'], ['embellir', 'beau'], ['folie', 'fou'], ['mollesse', 'mou'], ['renouveler', 'nouveau'],
    ['ouvrier', 'œuvre'], ['populaire', 'peuple'], ['visible', 'voir'],
];
/** Même famille, le radical se voit. */
const PAIRES_VISIBLES_Q06 = [
    ['joyeux', 'joie'], ['piéton', 'pied'], ['jeunesse', 'jeune'], ['refroidir', 'froid'], ['allaiter', 'lait'],
    ['éclairer', 'clair'], ['atterrir', 'terre'], ['mûrir', 'mûr'],
];
const paireQ06 = ([long, court]) => `« ${long} » et « ${court} »`;
const genQ06 = () => {
    const lignes = shuffle([
        [randomChoice(FAUSSES_PAIRES_Q06), 1],
        [randomChoice(PAIRES_ALTERNEES_Q06), 0],
        [randomChoice(PAIRES_VISIBLES_Q06), 0],
    ]);
    const mots = lignes.flatMap(([p]) => p);
    verifier(new Set(mots).size === 6, `Q06, six mots différents : ${mots.join(', ')}`);
    return makeVraiFaux(lignes.map(([p]) => paireQ06(p)), lignes.map(([, colonne]) => colonne),
        '<p>Indiquer si les mots proposés sont de la même famille ou non.</p>', { ...opts(L06), colonnes: COLONNES_Q06 });
};

/**
 * Q07 : Déduire le sens d'un mot composé du sens de deux autres.
 * Item : « Si l'« hypotension » signifie qu'on a une tension artérielle inférieure à la normale. Si un
 *   « thermomètre » est un instrument qui sert à mesurer les températures. Que signifie donc « être en
 *   hypothermie » ? » ; Que le thermomètre est plus petit que la normale. ; Que la tension est
 *   inférieure à la normale. ; Que la température est inférieure à la normale. (juste) ; Que la
 *   température est supérieure à la normale.
 * Domaine : le premier mot donne le préfixe, hypo- (inférieur) ou hyper- (supérieur), sur tension,
 *   thermie ou glycémie (le taux de sucre dans le sang) ; le second donne l'élément à combiner, therm-
 *   (thermomètre, thermostat) ou tensio- (tensiomètre) ; le mot à expliquer réunit les deux, jamais le
 *   premier mot lui-même. 12 variantes, dont l'item. « Faire de l'hypotension » et « faire de
 *   l'hypertension » remplacent « être en… », plus rare pour la tension, TRANCHÉ.
 * Erreurs : « Que le thermomètre est plus petit… » : le second mot pris en entier au lieu de son
 *   élément, le préfixe rendu par « plus petit » ; « Que la tension est inférieure… » : le radical du
 *   premier mot gardé au lieu de l'élément du second ; « Que la température est supérieure… » : le sens
 *   du préfixe inversé (hypo- pris pour hyper-). Chaque variante refait les trois.
 */
const L07 = 'Déduire le sens d\'un mot composé du sens de deux autres';
const HYPO = { prefixe: 'hypo', compare: 'inférieur', taille: 'petit' };
const HYPER = { prefixe: 'hyper', compare: 'supérieur', taille: 'grand' };
/** Une grandeur du corps : son nom, son article, son genre (pour l'accord d'« inférieur »). */
const TENSION = { nom: 'tension', article: 'la', feminin: true, cible: (p) => `faire de l'${p}tension` };
const TEMPERATURE = { nom: 'température', article: 'la', feminin: true, cible: (p) => `être en ${p}thermie` };
const SUCRE = { nom: 'taux de sucre', article: 'le', feminin: false };
const accord = (grandeur, sens) => `${sens.compare}${grandeur.feminin ? 'e' : ''}`;
/** Le premier mot : il donne le préfixe. */
const PREMIERS_Q07 = [HYPO, HYPER].flatMap((sens) => [
    { sens, grandeur: TENSION, mot: `${sens.prefixe}tension`, def: `une tension artérielle ${sens.compare}e à la normale` },
    { sens, grandeur: TEMPERATURE, mot: `${sens.prefixe}thermie`, def: `une température du corps ${sens.compare}e à la normale` },
    { sens, grandeur: SUCRE, mot: `${sens.prefixe}glycémie`, def: `un taux de sucre dans le sang ${sens.compare} à la normale` },
]);
/** Le second mot : il donne l'élément à combiner. */
const SECONDS_Q07 = [
    { grandeur: TEMPERATURE, mot: 'thermomètre', def: 'un instrument qui sert à mesurer les températures' },
    { grandeur: TEMPERATURE, mot: 'thermostat', def: 'un appareil qui sert à régler la température' },
    { grandeur: TENSION, mot: 'tensiomètre', def: 'un instrument qui sert à mesurer la tension artérielle' },
];
const DOMAINE_Q07 = PREMIERS_Q07.flatMap((premier) => SECONDS_Q07
    .filter((second) => second.grandeur !== premier.grandeur)
    .map((second) => ({ premier, second })));
const genQ07 = () => {
    const { premier, second } = randomChoice(DOMAINE_Q07);
    const { sens } = premier;
    const inverse = sens === HYPO ? HYPER : HYPO;
    const g = second.grandeur;
    const phrase = (grandeur, s) => `Que ${grandeur.article} ${grandeur.nom} est ${accord(grandeur, s)} à la normale.`;
    const juste = phrase(g, sens);
    return qcm(juste, [
        `Que le ${second.mot} est plus ${sens.taille} que la normale.`,
        phrase(premier.grandeur, sens),
        phrase(g, inverse),
    ], `<p>Si l'« ${premier.mot} » signifie qu'on a ${premier.def}.</p>`
        + `<p>Si un « ${second.mot} » est ${second.def}.</p>`
        + `<p>Que signifie donc « ${g.cible(sens.prefixe)} » ?</p>`, L07);
};

/**
 * Q08 : Identifier le thème commun d'une liste de mots (champ lexical).
 * Item : « « gouverner, autorité, président, reine ». Ces mots appartiennent au thème… » ; du conte.,
 *   du pouvoir. (juste), de la beauté., de la richesse.
 * Domaine : quatre mots courants d'un même thème ; trois autres thèmes, chacun appelé par un ou deux
 *   des mots seulement, par un autre sens ou une association courante (voile et le mariage, phare et
 *   l'automobile, sapin et Noël, avocat et les fruits, gel et la coiffure). 10 listes, dont l'item.
 * Erreurs : du conte. et de la beauté. : le thème d'un seul mot, reine (les reines des contes, une
 *   reine de beauté), pris pour celui de la liste ; de la richesse. : une association de reine et de
 *   président, qui ne vaut ni pour gouverner ni pour autorité. Chaque variante a trois thèmes de ce
 *   genre, aucun ne convient aux quatre mots.
 */
const L08 = 'Identifier le thème commun d\'une liste de mots';
/** [quatre mots, le thème juste, trois thèmes appelés par un ou deux des mots seulement] */
const LISTES_Q08 = [
    [['gouverner', 'autorité', 'président', 'reine'], 'du pouvoir.', ['du conte.', 'de la beauté.', 'de la richesse.']],
    [['vague', 'phare', 'voile', 'marée'], 'de la mer.', ['de l\'automobile.', 'du mariage.', 'de la météo.']],
    [['frisson', 'angoisse', 'terreur', 'cauchemar'], 'de la peur.', ['du froid.', 'du sommeil.', 'de la tristesse.']],
    [['trousse', 'récréation', 'élève', 'dictée'], 'de l\'école.', ['de la toilette.', 'du jeu.', 'de l\'orthographe.']],
    [['juge', 'avocat', 'tribunal', 'peine'], 'de la justice.', ['des fruits.', 'de la tristesse.', 'du sport.']],
    [['chêne', 'clairière', 'sapin', 'bûcheron'], 'de la forêt.', ['de Noël.', 'du conte.', 'de la lumière.']],
    [['étoile', 'lune', 'obscurité', 'chouette'], 'de la nuit.', ['du cinéma.', 'des oiseaux.', 'de l\'espace.']],
    [['pinceau', 'toile', 'palette', 'tableau'], 'de la peinture.', ['de l\'école.', 'du camping.', 'du maquillage.']],
    [['neige', 'flocon', 'gel', 'patiner'], 'de l\'hiver.', ['de la coiffure.', 'des céréales.', 'de la cuisine.']],
    [['bataille', 'soldat', 'armée', 'victoire'], 'de la guerre.', ['du sport.', 'des jeux de cartes.', 'des jouets.']],
];
const genQ08 = () => {
    const [mots, juste, themes] = randomChoice(LISTES_Q08);
    return qcm(juste, themes, `<p>« ${mots.join(', ')} ».</p><p>Ces mots appartiennent au thème…</p>`, L08);
};

/**
 * Q09 : Trouver la famille d'un mot au radical peu visible, grâce au contexte.
 * Item : « « Mon père est crédule : je lui ai dit que j'avais eu 20/20 en endurance et il a pensé que
 *   c'était vrai ! » D'après cette phrase, « crédule » est de la même famille que… » ; craindre.,
 *   croire. (juste), cruel., crudité.
 * Domaine : une phrase d'un collégien où un mot au radical peu visible (créd-, fril-, vigil-, fragil-,
 *   noct-) s'explique par la suite de la phrase ; le juste est le mot de sa famille que ce sens désigne
 *   (croire, froid, veiller, fracture, nuit), et les trois intrus commencent par les mêmes lettres que
 *   lui. 10 phrases, dont l'item. Comme dans l'item, « il » ou « elle » ne reprend jamais un prénom
 *   (mon père, mon oncle, cette équipe).
 * Erreurs : craindre., cruel., crudité. : un mot choisi sur ses premières lettres (cr-), sans le sens
 *   que la phrase donne (crédule : qui croit tout). Les intrus des variantes commencent comme le juste,
 *   et l'un d'eux lui ressemble (vieillir pour veiller, sommet pour sommeil, nuire pour nuit).
 */
const L09 = 'Trouver la famille d\'un mot au radical peu visible, grâce au contexte';
/** [la phrase, le mot, le mot juste de sa famille, trois intrus qui commencent comme lui] */
const PHRASES_Q09 = [
    ['Mon père est crédule : je lui ai dit que j\'avais eu 20/20 en endurance et il a pensé que c\'était vrai !',
        'crédule', 'croire.', ['craindre.', 'cruel.', 'crudité.']],
    ['Mon oncle est très frileux : il porte un bonnet même au printemps !', 'frileux', 'froid.', ['fraise.', 'frapper.', 'frère.']],
    ['Cette excuse est risible : personne dans la classe ne l\'a prise au sérieux !', 'risible', 'rire.', ['rivière.', 'rideau.', 'riche.']],
    ['Mon grand-père est très vigilant : il remarque tout ce qui se passe dans la rue.', 'vigilant', 'veiller.', ['vieillir.', 'vivre.', 'vider.']],
    ['Cette équipe est invincible : elle a gagné tous ses matchs cette année.', 'invincible', 'vaincre.', ['vague.', 'valise.', 'vanter.']],
    ['Mon frère souffre d\'insomnie : il n\'arrive presque jamais à dormir la nuit.', 'insomnie', 'sommeil.', ['sommet.', 'soleil.', 'sombre.']],
    ['Mon chat est nocturne : il dort toute la journée et part chasser quand il fait noir.', 'nocturne', 'nuit.', ['nuage.', 'nuire.', 'nuque.']],
    ['Ce verre est fragile : il s\'est brisé en tombant de la table.', 'fragile', 'fracture.', ['frite.', 'frotter.', 'front.']],
    ['Ce pont est mobile : il se soulève pour laisser passer les bateaux.', 'mobile', 'mouvement.', ['moquer.', 'montrer.', 'mordre.']],
    ['Cette eau n\'est pas potable : il ne faut surtout pas la boire.', 'potable', 'potion.', ['pomme.', 'poli.', 'poste.']],
];
const genQ09 = () => {
    const [phrase, mot, juste, intrus] = randomChoice(PHRASES_Q09);
    verifier(phrase.includes(mot), `Q09, « ${mot} » est dans la phrase`);
    return qcm(juste, intrus,
        `<p>« ${phrase} »</p><p>D'après cette phrase, « ${mot} » est de la même famille que…</p>`, L09);
};

/**
 * Q10 : Trouver l'intrus parmi des mots de sens proche.
 * Item : « Tous les mots ci-dessous ont un sens proche sauf un. Lequel ? » ; vaillance, hardiesse,
 *   témérité, traitrise (juste ; orthographe rectifiée, sans accent circonflexe, comme l'item).
 * Domaine : trois noms de sens proche (une qualité, un sentiment, un état), dont un rare pour un élève
 *   de 4e (hardiesse, mélancolie, allégresse, dextérité, droiture…) et souvent un qui porte une nuance
 *   (témérité, un courage qui va trop loin) ; l'intrus est un nom du même domaine, d'un autre sens (une
 *   autre qualité, un autre sentiment), jamais un contraire. 12 listes, dont l'item.
 * Erreurs : hardiesse ou vaillance : un mot rare pris pour l'intrus parce qu'il n'est pas compris ;
 *   témérité : sa nuance d'excès prise pour un autre sens ; traitrise, un défaut du même domaine (la
 *   conduite au combat), n'est pas vue comme l'intrus. Chaque variante garde un mot rare et un intrus
 *   du même domaine.
 */
const L10 = 'Trouver l\'intrus parmi des mots de sens proche';
/** [trois noms de sens proche, l'intrus du même domaine] */
const LISTES_Q10 = [
    [['vaillance', 'hardiesse', 'témérité'], 'traitrise'],
    [['chagrin', 'tristesse', 'mélancolie'], 'colère'],
    [['joie', 'gaieté', 'allégresse'], 'fierté'],
    [['peur', 'frayeur', 'épouvante'], 'honte'],
    [['colère', 'fureur', 'rage'], 'ennui'],
    [['bonté', 'gentillesse', 'bienveillance'], 'franchise'],
    [['paresse', 'fainéantise', 'nonchalance'], 'timidité'],
    [['calme', 'tranquillité', 'sérénité'], 'fatigue'],
    [['habileté', 'adresse', 'dextérité'], 'force'],
    [['étonnement', 'surprise', 'stupéfaction'], 'inquiétude'],
    [['honnêteté', 'loyauté', 'droiture'], 'générosité'],
    [['fatigue', 'épuisement', 'lassitude'], 'faim'],
];
const genQ10 = () => {
    const [proches, intrus] = randomChoice(LISTES_Q10);
    return qcm(intrus, proches, '<p>Tous les mots ci-dessous ont un sens proche sauf un. Lequel ?</p>', L10);
};

/**
 * Q11 : Caractériser le sentiment du narrateur grâce à un champ lexical.
 * Item : « « J'ai sans cesse cette sensation affreuse d'un danger menaçant, cette appréhension d'un
 *   malheur qui vient ou de la mort qui approche, ce pressentiment qui est sans doute l'atteinte d'un
 *   mal encore inconnu. » Le Horla, Maupassant. Dans cet extrait, celui qui dit « je » semble… » ;
 *   égaré., menaçant., anxieux. (juste), serein.
 * Domaine : un court texte à la première personne (une quarantaine de mots, comme l'item), dont le
 *   champ lexical dit un sentiment : l'item, puis neuf textes écrits pour le QCM de suivi (la tristesse,
 *   la joie, la colère, la peur, l'émerveillement, le soulagement, la honte, la fierté, l'impatience),
 *   sans auteur ni titre, pour n'attribuer à personne une phrase qu'il n'a pas écrite ; « Dans ce
 *   texte » y remplace « Dans cet extrait », TRANCHÉ. 10 variantes.
 * Erreurs : menaçant. : un adjectif recopié du texte, qui y qualifie autre chose que le narrateur (un
 *   danger menaçant) ; serein. : le contraire du sentiment ; égaré. : un état voisin que le texte ne
 *   dit pas. Chaque texte a son adjectif qui qualifie une chose (pesant, injuste, ridicule, lent…),
 *   son contraire et son état voisin.
 */
const L11 = 'Caractériser le sentiment du narrateur grâce à un champ lexical';
/** [le texte, sa source (l'item seul en a une), le sentiment, l'adjectif recopié, le contraire, l'état voisin] */
const TEXTES_Q11 = [
    ['J\'ai sans cesse cette sensation affreuse d\'un danger menaçant, cette appréhension d\'un malheur qui vient ou de la mort qui approche, ce pressentiment qui est sans doute l\'atteinte d\'un mal encore inconnu.',
        '<i>Le Horla</i>, Maupassant', 'anxieux.', 'menaçant.', 'serein.', 'égaré.'],
    ['Depuis ton départ, les journées me paraissent grises et sans fin. Je n\'ai plus le cœur à rien : je reste des heures à la fenêtre, les yeux pleins de larmes, et le silence pesant de la maison me serre la gorge.',
        null, 'triste.', 'pesant.', 'joyeux.', 'fatigué.'],
    ['Quand j\'ai ouvert la lettre, mon cœur s\'est mis à bondir. J\'ai couru dans tout l\'appartement en riant, j\'avais envie de chanter et d\'embrasser tout le monde : cette nouvelle merveilleuse illuminait enfin ma journée.',
        null, 'joyeux.', 'merveilleux.', 'triste.', 'pressé.'],
    ['Mes mains tremblaient et mon visage était brûlant. J\'avais envie de hurler, de claquer toutes les portes : cette remarque injuste me mettait hors de moi, et je serrais les poings pour ne pas exploser.',
        null, 'furieux.', 'injuste.', 'calme.', 'effrayé.'],
    ['Dans le couloir obscur, chaque craquement me faisait sursauter. Mon cœur battait à tout rompre, mes jambes refusaient d\'avancer et je n\'osais plus respirer, persuadé qu\'une présence invisible me guettait dans l\'ombre.',
        null, 'terrifié.', 'invisible.', 'rassuré.', 'essoufflé.'],
    ['Lorsque le rideau s\'est levé, je suis resté bouche bée. Les couleurs éclatantes, la musique, les danseurs qui semblaient voler : je n\'avais jamais rien vu d\'aussi beau, et j\'en oubliais même de respirer.',
        null, 'émerveillé.', 'éclatant.', 'indifférent.', 'distrait.'],
    ['Quand j\'ai enfin vu mon chien sortir du bois, toute ma peur s\'est envolée. J\'ai respiré profondément, mes épaules se sont détendues et le sourire est revenu sur mon visage après ces deux heures interminables.',
        null, 'soulagé.', 'interminable.', 'angoissé.', 'épuisé.'],
    ['J\'aurais voulu disparaître sous terre. Mes joues étaient brûlantes, je gardais les yeux baissés et je n\'osais plus regarder personne depuis que mon mensonge ridicule avait été découvert devant toute la classe.',
        null, 'honteux.', 'ridicule.', 'fier.', 'malade.'],
    ['Je tenais la médaille dans ma main et je ne pouvais pas m\'empêcher de sourire. Toute ma famille m\'applaudissait : après des mois d\'entraînement difficile, j\'avais enfin réussi ce que j\'avais tant espéré.',
        null, 'fier.', 'difficile.', 'honteux.', 'fatigué.'],
    ['Je regardais l\'horloge toutes les trente secondes. Je ne tenais pas en place, je tapais du pied, je faisais les cent pas sur le quai : pourquoi ce train si lent n\'arrivait-il pas enfin ?',
        null, 'impatient.', 'lent.', 'calme.', 'distrait.'],
];
const genQ11 = () => {
    const [texte, source, juste, recopie, contraire, voisin] = randomChoice(TEXTES_Q11);
    verifier(texte.includes(recopie.replace('.', '').slice(0, 5)), `Q11, « ${recopie} » vient du texte`);
    const consigne = source ? 'Dans cet extrait, celui qui dit « je » semble…' : 'Dans ce texte, celui qui dit « je » semble…';
    return qcm(juste, [recopie, contraire, voisin],
        `<p>« ${texte} »${source ? ` ${source}` : ''}</p><p>${consigne}</p>`, L11);
};

/**
 * Q12 : Trouver le mot qui appartient à un thème donné.
 * Item : « Trouver le mot qui appartient au thème de la mer. » ; mercure, marin (juste), maternité,
 *   mérite.
 * Domaine : un thème nommé par un mot courant (la mer, le soleil, l'eau, l'école, l'œil…) ; le juste
 *   est un mot du thème qui ne contient pas ce mot, souvent de radical savant (marin, solaire, scolaire,
 *   aquarium, oculiste, cardiaque) ; les trois intrus contiennent le mot du thème ou lui ressemblent
 *   sans en être (mercredi, beauté, écorce, orteil, chœur). 10 thèmes, dont l'item.
 * Erreurs : mercure, maternité, mérite : un mot choisi parce qu'il contient « mer » ou lui ressemble
 *   (mer-cure, mér-ite, ma-ternité), sans son sens ; marin n'est pas reconnu, mar- ne ressemblant pas
 *   à « mer ». Chaque variante fait de même.
 */
const L12 = 'Trouver le mot qui appartient à un thème donné';
/** [le thème, le mot juste, trois intrus qui ressemblent au mot du thème] */
const THEMES_Q12 = [
    ['de la mer', 'marin', ['mercure', 'maternité', 'mérite']],
    ['de la mer', 'marée', ['mercredi', 'merci', 'merle']],
    ['du soleil', 'solaire', ['soldat', 'solide', 'solution']],
    ['de l\'eau', 'aquarium', ['beauté', 'bureau', 'chapeau']],
    ['du cheval', 'équitation', ['chevelure', 'chèvre', 'chevet']],
    ['du feu', 'flamme', ['feuille', 'feutre', 'feuilleton']],
    ['de l\'école', 'scolaire', ['écorce', 'écologie', 'écureuil']],
    ['du livre', 'bibliothèque', ['livrer', 'lièvre', 'libre']],
    ['de l\'œil', 'oculiste', ['orteil', 'oreille', 'oseille']],
    ['du cœur', 'cardiaque', ['chœur', 'coureur', 'couleur']],
];
const genQ12 = () => {
    const [theme, juste, intrus] = randomChoice(THEMES_Q12);
    return qcm(juste, intrus, `<p>Trouver le mot qui appartient au thème ${theme}.</p>`, L12);
};

/**
 * Q13 : Dire si un adjectif donne une image positive ou négative (tableau à cocher).
 * Item : « Indiquer pour chaque adjectif s'il donne une image positive ou négative de l'attitude d'une
 *   personne. » ; colonnes « Image positive » et « Image négative » ; prétentieux (négative), modeste
 *   (positive), orgueilleux (négative), humble (positive).
 * Domaine : quatre adjectifs de caractère sur un même axe, deux qui donnent une image positive et deux
 *   négative, sans contexte qui renverse le jugement (curieux, naïf, ambitieux, fier, rusé écartés) ;
 *   jamais deux contraires de même radical dans un tableau (poli et impoli, loyal et déloyal). 8 axes :
 *   l'orgueil, le courage, l'honnêteté, la politesse, le calme, le travail, la bonté, la générosité ;
 *   25 tableaux de quatre adjectifs différents, dont l'item, et l'ordre des lignes est tiré.
 * Erreurs : prétentieux, orgueilleux cochés « positive » : l'assurance prise pour une qualité ; modeste,
 *   humble cochés « négative » : leur autre sens (un salaire modeste, d'humbles origines), qui fait
 *   penser à la pauvreté. Chaque axe garde un mot rare ou à double sens (courtois, brave, colérique,
 *   négligent, sournois, altruiste).
 */
const L13 = 'Dire si un adjectif donne une image positive ou négative';
const COLONNES_Q13 = ['Image positive', 'Image négative'];
/** [adjectifs qui donnent une image positive, adjectifs qui donnent une image négative] */
const AXES_Q13 = [
    [['modeste', 'humble'], ['prétentieux', 'orgueilleux', 'vaniteux', 'arrogant']],
    [['courageux', 'brave'], ['lâche', 'peureux']],
    [['honnête', 'sincère', 'loyal'], ['hypocrite', 'menteur', 'sournois']],
    [['poli', 'courtois'], ['grossier', 'insolent']],
    [['patient', 'calme'], ['colérique', 'agressif']],
    [['travailleur', 'appliqué', 'sérieux'], ['paresseux', 'négligent']],
    [['bienveillant', 'aimable', 'attentionné'], ['méchant', 'cruel']],
    [['généreux', 'altruiste'], ['avare', 'égoïste']],
];
const genQ13 = () => {
    const [positifs, negatifs] = randomChoice(AXES_Q13);
    const lignes = shuffle([
        ...shuffle(positifs).slice(0, 2).map((a) => [a, 0]),
        ...shuffle(negatifs).slice(0, 2).map((a) => [a, 1]),
    ]);
    verifier(lignes.length === 4 && lignes.filter(([, c]) => c === 0).length === 2, 'Q13, deux adjectifs de chaque image');
    return makeVraiFaux(lignes.map(([a]) => a), lignes.map(([, c]) => c),
        '<p>Indiquer pour chaque adjectif s\'il donne une image positive ou négative de l\'attitude d\'une personne.</p>',
        { ...opts(L13), colonnes: COLONNES_Q13 });
};

/**
 * Q14 : Associer chaque sens d'un mot polysémique à sa phrase (tableau à cocher).
 * Item : « Dans les quatre phrases ci-dessous, le mot souligné a une signification différente. Associer
 *   chaque phrase à la signification qui convient. » ; colonnes Facture, Trace écrite, Nom d'un son,
 *   Résultat ; « Mattéo a pris des [notes] en regardant la vidéo. » (Trace écrite), « Emma a obtenu la
 *   [note] de 15/20 en mathématiques. » (Résultat), « Cette musique commence par la [note] fa. » (Nom
 *   d'un son), « Paolo a payé sa [note] d'hôtel en partant. » (Facture). Le mot souligné sur la capture
 *   est entre crochets, la marque de la base.
 * Domaine : un nom courant à quatre sens bien distincts (note, carte, bouton, glace, pièce, classe,
 *   marche, tour, mousse, chaîne), quatre phrases de collégien dont le contexte fixe le sens, une colonne
 *   par sens, chaque colonne juste une fois ; l'ordre des phrases est tiré. 10 mots, dont l'item. Un
 *   prénom n'est jamais repris par un pronom.
 * Erreurs : un sens pris pour un autre quand la phrase est mal lue (la note d'hôtel cochée « Résultat »,
 *   la note de 15/20 « Facture »), ou le sens le plus courant coché partout. Chaque mot a deux sens que
 *   seule la phrase sépare (la salle et le groupe d'élèves pour classe, la promenade et le défilé pour
 *   marche, le dessert et les bulles pour mousse).
 */
const L14 = 'Associer chaque sens d\'un mot polysémique à sa phrase';
/** [le mot, ses quatre sens (les colonnes), les quatre phrases dans l'ordre des sens] */
const MOTS_Q14 = [
    ['note', ['Facture', 'Trace écrite', 'Nom d\'un son', 'Résultat'], [
        'Paolo a payé sa [note] d\'hôtel en partant.', 'Mattéo a pris des [notes] en regardant la vidéo.',
        'Cette musique commence par la [note] fa.', 'Emma a obtenu la [note] de 15/20 en mathématiques.']],
    ['carte', ['Plan d\'un lieu', 'Liste des plats', 'Objet d\'un jeu', 'Moyen de paiement'], [
        'Léa a déplié la [carte] pour trouver le chemin du refuge.', 'Au restaurant, Hugo a lu toute la [carte] avant de choisir un dessert.',
        'Inès a tiré une [carte] et a gagné la partie.', 'À la caisse, Noah a payé ses courses avec sa [carte].']],
    ['bouton', ['Attache d\'un vêtement', 'Petite boule sur la peau', 'Fleur pas encore ouverte', 'Commande d\'un appareil'], [
        'Le [bouton] de la veste de Lucas s\'est décousu.', 'Ce matin, Chloé avait un [bouton] sur le front.',
        'Le rosier du jardin porte déjà un [bouton] prêt à s\'ouvrir.', 'Pour allumer la machine, Adam appuie sur le [bouton] rouge.']],
    ['glace', ['Dessert', 'Miroir', 'Eau gelée', 'Vitre d\'une voiture'], [
        'Après le repas, Jade a mangé une [glace] à la fraise.', 'Avant de sortir, Tom se regarde dans la [glace] de l\'entrée.',
        'En hiver, la [glace] recouvre le lac du village.', 'Dans la voiture, Malik a baissé la [glace] pour respirer un peu d\'air frais.']],
    ['pièce', ['Monnaie', 'Partie d\'un logement', 'Spectacle de théâtre', 'Morceau d\'un puzzle'], [
        'Lina a glissé une [pièce] de deux euros dans la machine.', 'Le nouvel appartement de Yanis a une [pièce] de plus.',
        'Les élèves de 4e ont joué une [pièce] de Molière.', 'Il manque une [pièce] au puzzle de Nathan.']],
    ['classe', ['Salle de cours', 'Groupe d\'élèves', 'Élégance', 'Catégorie de places'], [
        'Les élèves entrent dans la [classe] à huit heures.', 'Toute la [classe] de 4e B part en voyage en Italie.',
        'Avec son costume, Samuel a beaucoup de [classe].', 'Dans le train, Clara voyage en première [classe].']],
    ['marche', ['Partie d\'un escalier', 'Promenade à pied', 'Défilé', 'Fonctionnement'], [
        'Attention à la dernière [marche] de l\'escalier !', 'Après le repas, Léo fait une petite [marche] dans le parc.',
        'Des milliers de personnes ont participé à la [marche] pour le climat.', 'Avant de partir, Zoé vérifie que le lave-linge est bien en [marche].']],
    ['tour', ['Bâtiment très haut', 'Moment de jouer', 'Numéro de magie', 'Promenade'], [
        'La [tour] Eiffel mesure plus de trois cents mètres.', 'C\'est au [tour] d\'Émile de lancer les dés.',
        'Le magicien a fait un [tour] avec des cartes.', 'Après le dîner, Maël fait un [tour] dans le quartier.']],
    ['mousse', ['Petite plante', 'Dessert léger', 'Bulles', 'Jeune marin'], [
        'Dans la forêt, Anaïs s\'assoit sur la [mousse] verte.', 'Pour le dessert, Hugo a préparé une [mousse] au chocolat.',
        'Le savon fait beaucoup de [mousse] dans la baignoire.', 'Sur le bateau, le [mousse] lave le pont tous les matins.']],
    ['chaîne', ['Bijou', 'Station de télévision', 'Pièce d\'un vélo', 'Suite de montagnes'], [
        'Pour son anniversaire, Lou a reçu une [chaîne] en argent.', 'Le match passe ce soir sur une autre [chaîne].',
        'La [chaîne] du vélo de Rayan a déraillé.', 'Les randonneurs traversent une [chaîne] de montagnes.']],
];
const genQ14 = () => {
    const [mot, sens, phrases] = randomChoice(MOTS_Q14);
    verifier(sens.length === 4 && phrases.length === 4, `Q14, quatre sens de « ${mot} »`);
    verifier(phrases.every((p) => (p.match(/\[/g) || []).length === 1 && p.includes(`[${mot}`)), `Q14, « ${mot} » marqué une fois par phrase`);
    const lignes = shuffle(phrases.map((phrase, colonne) => [phrase, colonne]));
    return makeVraiFaux(lignes.map(([p]) => p), lignes.map(([, c]) => c),
        '<p>Dans les quatre phrases ci-dessous, le mot souligné a une signification différente. Associer chaque phrase à la signification qui convient.</p>',
        { ...opts(L14), colonnes: sens });
};

/**
 * Q15 : Remplacer le verbe « faire » par le verbe précis qui convient.
 * Item : « Dans la phrase : « Ce musicien fait d'admirables symphonies. », remplacer « fait » par le
 *   verbe qui convient. » (« fait » souligné) ; compose (juste), fonde, négocie, agence.
 * Domaine : une phrase où « fait » a un complément qui appelle un verbe précis (des symphonies :
 *   compose ; un discours : prononce ; une enquête : mène ; une faute : commet) ; trois intrus, des
 *   verbes précis d'autres domaines, dont aucun ne remplace « fait » dans cette phrase, vérifié phrase
 *   par phrase (composer, écarté pour un tableau ou une lettre ; prononcer, pour une lettre). 12 phrases,
 *   dont l'item.
 * Erreurs : fonde, négocie, agence : un verbe précis qui ne va pas avec le complément (on fonde une
 *   ville, on négocie un prix, on agence une pièce) : le complément n'est pas lu. Chaque variante donne
 *   trois verbes précis d'autres domaines.
 */
const L15 = 'Remplacer le verbe « faire » par le verbe précis qui convient';
/** [le début de la phrase, la suite après « fait », le verbe juste, trois verbes d'autres domaines] */
const PHRASES_Q15 = [
    ['Ce musicien', 'd\'admirables symphonies', 'compose', ['fonde', 'négocie', 'agence']],
    ['Ce peintre', 'de magnifiques paysages', 'peint', ['rédige', 'cultive', 'prononce']],
    ['Le maire', 'un long discours', 'prononce', ['peint', 'creuse', 'cultive']],
    ['Le détective', 'une enquête difficile', 'mène', ['coud', 'cultive', 'peint']],
    ['Mon grand-père', 'des tomates dans son jardin', 'cultive', ['rédige', 'prononce', 'coud']],
    ['Cette cinéaste', 'un film sur les océans', 'réalise', ['cultive', 'bâtit', 'prononce']],
    ['Le chien', 'un trou sous la barrière', 'creuse', ['compose', 'rédige', 'prononce']],
    ['Cet élève', 'souvent la même faute', 'commet', ['cultive', 'bâtit', 'creuse']],
    ['L\'hirondelle', 'son nid sous le toit', 'construit', ['rédige', 'prononce', 'compose']],
    ['La couturière', 'une robe de soirée', 'coud', ['fonde', 'prononce', 'creuse']],
    ['Ma sœur', 'une lettre à sa correspondante', 'rédige', ['cultive', 'bâtit', 'peint']],
    ['Le maçon', 'un mur en pierre', 'bâtit', ['rédige', 'coud', 'prononce']],
];
const genQ15 = () => {
    const [debut, suite, juste, intrus] = randomChoice(PHRASES_Q15);
    return qcm(juste, intrus,
        `<p>Dans la phrase : « ${debut} <u>fait</u> ${suite}. », remplacer « fait » par le verbe qui convient.</p>`, L15);
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_tableau: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_tableau: genQ13,
    q14_tableau: genQ14,
    q15_qcm_unique: genQ15,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: 'espoir' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: 'une action.' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: 'engranger' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: 'in-' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: 'chouette' },
    {
        questionNum: 'Q06', formatKey: 'q06_tableau', libelle: L06,
        bonneReponseOfficielle: '[« carapace » et « car »|Ne sont pas de la même famille][« dénouer » et « nœud »|Sont de la même famille][« joyeux » et « joie »|Sont de la même famille]',
    },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '… température… inférieure… .' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: 'du pouvoir.' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: 'croire.' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: 'traitrise' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: 'anxieux.' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: 'marin' },
    {
        questionNum: 'Q13', formatKey: 'q13_tableau', libelle: L13,
        bonneReponseOfficielle: '[prétentieux|Image négative][modeste|Image positive][orgueilleux|Image négative][humble|Image positive]',
    },
    {
        questionNum: 'Q14', formatKey: 'q14_tableau', libelle: L14,
        bonneReponseOfficielle: '[Mattéo a pris des [notes] en regardant la vidéo.|Trace écrite] [Emma a obtenu la [note] de 15/20 en mathématiques.|Résultat] [Paolo a payé sa [note] d’hôtel en partant.|Facture] [Cette musique commence par la [note] fa.|Nom d’un son]',
    },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: 'compose' },
];

/** Le format de chaque carte : un QCM, ou un tableau à cocher pour Q06, Q13 et Q14. */
const formatLabel = (formatKey) => (formatKey.endsWith('_tableau') ? 'Tableau' : 'QCM');

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: formatLabel(formatKey) }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
